import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { ChevronUpDownIcon } from '@heroicons/react/24/solid';

/**
 * A customizable select component with support for single and multiple selection.
 * @param {Object} props - The component props.
 * @param {string} [props.className] - Additional CSS classes to apply to the component.
 * @param {boolean} [props.multiple] - Whether to allow multiple selections.
 * @param {Array} props.options - The options to display in the select dropdown.
 * @param {React.Ref} ref - The forwarded ref for the select element.
 * @returns {React.Element} The rendered Select component.
 */
export const Select = forwardRef(function Select(
  { className, options, multiple, ...props },
  ref
) {
  return (
    <div className="relative">
      <select
        ref={ref}
        multiple={multiple}
        {...props}
        className={clsx(
          className,
          'block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-gray-900 focus:border-blue-500 focus:ring-blue-500',
          'appearance-none !pr-8 truncate',
          props.disabled && 'bg-gray-100 text-gray-500 cursor-not-allowed'
        )}
      >
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
        <ChevronUpDownIcon width={20} height={20} />
      </div>
    </div>
  );
});

Select.propTypes = {
  className: PropTypes.string,
  multiple: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
};
